/* eslint-disable max-lines */
// deps
import { useCallback, useContext, useEffect, useRef, useState } from "react";

// internal-deps
import { Err, Ok, Some } from "@swiggy-private/js-fp";
import { isEmpty, useMountedRef, SESSION_STORAGE_KEYS, safeJsonParse, RouteContext } from "@swiggy-private/core-ui";
import {
    AnalyticsContext,
    ApiContext,
    CheckoutContext,
    ConfigContext,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    useSessionStorage,
    CheckoutRoute,
} from "@swiggy-private/checkout-core";

// interfaces
import { SplitPayMethodValues, TURBO_VARIANT } from "@payment-client/interfaces/SplitPay";
import {
    ShowPaymentState,
    ShowPaymentCacheValue,
    UseShowPaymentsStateProps,
} from "@payment-client/interfaces/ShowPayments";

// hooks
import useCartKey from "./UseCartKey";

// helpers
import {
    canUseShowPaymentsDataFromCache,
    isBusinessLineDineout,
    isBusinessLinePayOnDelivery,
    isBusinessLineSwiggyMoney,
    isCategoryInstamart,
} from "@payment-client/helpers/Misc";
import { isTurboGpoPayload } from "@payment-client/helpers/PaymentState";
import { fetchPaymentsAndMeta } from "@payment-client/helpers/PaymentFetchHelper";
import { getTurboPayFallbackConfig } from "@payment-client/helpers/TurboPayment";
import { getGpoSource } from "@payment-client/helpers/PaymentFetchHelper";

// constants
import {
    DEFAULT_PAYMENTS_RESPONSE_KEY,
    SWIGGY_MONEY_PAYMENTS_RESPONSE_KEY,
} from "@payment-client/constants/StorageKeys";

const useShowPaymentsState = ({
    isTurboPaymentMethod = false,
    turboPayUniqueId = "",
    isTurboV2 = false,
    isBillToCompanySelected = false,
}: UseShowPaymentsStateProps): [ShowPaymentState, () => void, () => void] => {
    const apiContextValue = useContext(ApiContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { cachePaymentData } = useContext(ConfigContext);
    const { checkoutState } = useContext(CheckoutContext);
    const { route } = useContext(RouteContext);

    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();
    // Keep route name out of the GPO fetch effect deps; read latest value when a fetch actually runs.
    const routeNameRef = useRef(route?.name);
    routeNameRef.current = route?.name;
    const [state, setState] = useState<ShowPaymentState>({ loading: true, error: undefined });
    const { cartKey, error: cartKeyError } = useCartKey();

    const isSwiggyMoneyFlow = isBusinessLineSwiggyMoney();
    const isPayOnDeliveryFlow = isBusinessLinePayOnDelivery();
    const isInstamartPodFlow = isCategoryInstamart();
    const orderIdFromHeader = GlobalHeaderState.getHeader(SwgyHeaderKeysList.orderid);

    const paymentStorageKey = isSwiggyMoneyFlow ? SWIGGY_MONEY_PAYMENTS_RESPONSE_KEY : DEFAULT_PAYMENTS_RESPONSE_KEY;
    const [dataInCache, saveDataInCache] = useSessionStorage<ShowPaymentCacheValue | null>(paymentStorageKey, null);
    const [splitPaySelectedMethods] = useSessionStorage("splitpay_method", SplitPayMethodValues.DEFAULT as string);

    const apiHost = apiContextValue.host.unwrap();
    const canCachePaymentData = cachePaymentData?.unwrapOr(true);

    const isRefetch = useRef(false);
    const _onRefetchErrCb = useRef<Function>();

    const splitPayMethods = useRef(splitPaySelectedMethods);

    const reset = useCallback((): void => {
        saveDataInCache(null);
        setState({ loading: true, error: undefined });
    }, [saveDataInCache]);

    const refetch = useCallback((splitPayMethodsString?: string, onRefetchErrCb?: Function): void => {
        _onRefetchErrCb.current = onRefetchErrCb;
        isRefetch.current = true;
        splitPayMethods.current = splitPayMethodsString || "";
        setState({ loading: true, error: undefined });
    }, []);

    useEffect(() => {
        if (
            isTurboPaymentMethod &&
            (dataInCache?.turboPayUniqueId != turboPayUniqueId ||
                isBillToCompanySelected != dataInCache?.isBillToCompanySelected) &&
            !state.loading
        ) {
            setState({ loading: true, error: undefined });
        }
    }, [
        dataInCache?.turboPayUniqueId,
        isTurboPaymentMethod,
        state.loading,
        turboPayUniqueId,
        isBillToCompanySelected,
        dataInCache?.isBillToCompanySelected,
    ]);

    // When user navigates to PAYMENTS (back from a sub-route or remount from CONFIRM_ORDER),
    // ensure GPO is refetched if the cache has a turbo-only payload.
    useEffect(() => {
        if (
            route?.name === CheckoutRoute.PAYMENTS &&
            !isTurboPaymentMethod &&
            dataInCache &&
            isTurboGpoPayload(dataInCache.paymentResponse)
        ) {
            refetch();
        }
        // Missing deps is on purpose. We only want this effect to fire when the route changes
        // to PAYMENTS, not every time dataInCache or isTurboPaymentMethod changes (which
        // would cause refetches mid-session)
    }, [route?.name, refetch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // for pay on delivery flow, we need cartKey to make api call
        // fetch this cartkey using orderId.
        // Instamart pay on delivery does not need cartKey for the GPO call, so it is excluded here.
        if (isPayOnDeliveryFlow && !isInstamartPodFlow) {
            // if while fetching cart key you got error, then show generic error page
            if (!isEmpty(cartKeyError)) {
                setState({ loading: false, data: undefined, error: cartKeyError });
                return;
            }

            if (isEmpty(cartKey)) {
                return;
            }
        }

        const turboPayFallbackId = getTurboPayFallbackConfig();
        if (turboPayFallbackId === turboPayUniqueId) {
            setState({ loading: false, data: undefined, error: undefined });
            return;
        }

        if (dataInCache && state.loading && !isRefetch.current) {
            // not caching sm payments response , as it depends on selected recharge amount
            const canUseCache = canUseShowPaymentsDataFromCache({
                dataInCache,
                isSwiggyMoneyFlow,
                isTurboPaymentMethod,
                turboPayUniqueId,
                cartKey,
                checkoutState,
                isBillToCompanySelected,
            });

            if (canUseCache) {
                setState({
                    data: Ok({
                        paymentResponse: dataInCache.paymentResponse,
                        checkoutMetaInfo: Some(dataInCache.checkoutMetaInfo),
                    }),
                    loading: false,
                    error: undefined,
                });

                return;
            }
        }

        if (!state.loading || apiCallRef.current) {
            return;
        }

        apiCallRef.current = true;

        const fetchCheckoutMetaInfo = !(isSwiggyMoneyFlow || isPayOnDeliveryFlow || isBusinessLineDineout());

        // Read attendeeEmail from expense code data in session storage for GPO payload
        let billToCompanyInfoMeta: { attendeeEmail: string[] } | undefined;
        try {
            const expenseCodeData = safeJsonParse<{ attendeeEmail?: string[] }>(
                sessionStorage.getItem(SESSION_STORAGE_KEYS.EXPENSE_CODE_DATA) as string,
            );
            if (expenseCodeData?.attendeeEmail?.length) {
                billToCompanyInfoMeta = {
                    attendeeEmail: expenseCodeData.attendeeEmail,
                };
            }
        } catch (err) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.EXPENSE_CODE_SESSION_STORAGE_ERROR,
                error: err instanceof Error ? err.message : String(err),
            });
        }

        fetchPaymentsAndMeta({
            apiHost: apiHost,
            fetchCheckoutMetaInfo: fetchCheckoutMetaInfo,
            impressionEvent: impressionEvent,
            isRefetch: isRefetch.current,
            dataInCache: dataInCache,
            isUserEligibleForSingleClick: isTurboPaymentMethod,
            // in case of turbo flow, we need to pass splitPayMethods as default so that
            // the amount computations are always done without splitpay, this is because amountToPay can vary with/without splitpay
            // and filtering criteria of sufficient balance will break in both the cases
            splitPayMethods: isTurboPaymentMethod ? SplitPayMethodValues.DEFAULT : splitPayMethods.current,
            // TODO: turboVariant will be removed post scale up of cart 2.0
            turboVariant: isTurboV2 && isTurboPaymentMethod ? TURBO_VARIANT.TURBO_V2 : undefined,
            onRefetchErrCb: _onRefetchErrCb.current,
            isBillToCompanySelected,
            billToCompanyInfoMeta,
            turboPayUniqueId,
            // entry/trigger point of the GPO call, derived from the active route
            source: getGpoSource(routeNameRef.current, isTurboPaymentMethod),
        })
            .then(([payments, meta]) => {
                apiCallRef.current = false;
                if (isMountedRef.current) {
                    const data = payments.map((paymentResponse) => {
                        if (canCachePaymentData && !isSwiggyMoneyFlow) {
                            saveDataInCache({
                                paymentResponse,
                                checkoutMetaInfo: meta.isSome() ? meta.unwrap() : null,
                                key: cartKey ?? "",
                                turboPayUniqueId,
                                isBillToCompanySelected,
                            });
                        }

                        return {
                            paymentResponse,
                            checkoutMetaInfo: meta,
                        };
                    });
                    isRefetch.current = false;
                    setState({ loading: false, data, error: undefined });
                }
            })
            .catch((err) => {
                apiCallRef.current = false;
                if (isMountedRef.current) {
                    canCachePaymentData && saveDataInCache(null);
                    setState({ loading: false, data: Err(err), error: undefined });
                }
            });
    }, [
        state.loading,
        dataInCache,
        canCachePaymentData,
        apiHost,
        isMountedRef,
        saveDataInCache,
        impressionEvent,
        cartKey,
        isSwiggyMoneyFlow,
        isPayOnDeliveryFlow,
        isInstamartPodFlow,
        orderIdFromHeader,
        cartKeyError,
        isTurboPaymentMethod,
        turboPayUniqueId,
        checkoutState,
        isTurboV2,
        isBillToCompanySelected,
    ]);

    return [state, reset, refetch];
};

export default useShowPaymentsState;
